/**
 * nearest-neighbor Tm と簡易 F/R 提案（§5.7 I79–I81）。
 */

import { CODE_TO_CHAR, countGc, reverseComplementCodes } from '../sequence/nucleotideCodes';

const NN_DH: Record<string, number> = {
  AA: -7.9, TT: -7.9, AT: -7.2, TA: -7.2,
  CA: -8.5, TG: -8.5, GT: -8.4, AC: -8.4,
  CT: -7.8, AG: -7.8, GA: -8.2, TC: -8.2,
  CG: -10.6, GC: -9.8, GG: -8.0, CC: -8.0,
};

const NN_DS: Record<string, number> = {
  AA: -22.2, TT: -22.2, AT: -20.4, TA: -21.3,
  CA: -22.7, TG: -22.7, GT: -22.4, AC: -22.4,
  CT: -21.0, AG: -21.0, GA: -22.2, TC: -22.2,
  CG: -27.2, GC: -24.4, GG: -19.9, CC: -19.9,
};

export interface PrimerDesignSettings {
  targetTm: number;
  minLength: number;
  maxLength: number;
  minGc: number;
  maxGc: number;
  concNmol: number;
}

export const DEFAULT_PRIMER_SETTINGS: PrimerDesignSettings = {
  targetTm: 60,
  minLength: 18,
  maxLength: 28,
  minGc: 40,
  maxGc: 60,
  concNmol: 500,
};

export interface DesignedPrimer {
  sequence: string;
  start: number;
  end: number;
  tm: number;
  gc: number;
  strand: 1 | -1;
}

export function tmNearestNeighbor(codes: Uint8Array, concNmol = 500): number {
  if (codes.length < 8) return 0;
  let dh = 0;
  let ds = 0;
  for (let i = 0; i < codes.length - 1; i += 1) {
    const pair = `${CODE_TO_CHAR[codes[i]]}${CODE_TO_CHAR[codes[i + 1]]}`;
    dh += NN_DH[pair] ?? -8;
    ds += NN_DS[pair] ?? -22;
  }
  const conc = Math.max(50, Math.min(2000, concNmol)) * 1e-9;
  const tm = dh * 1000 / (ds + 1.987 * Math.log(conc / 4)) - 273.15;
  return Math.round(tm * 10) / 10;
}

export function gcPercentCodes(codes: Uint8Array): number {
  if (codes.length === 0) return 0;
  return (countGc(codes) / codes.length) * 100;
}

export function designPrimerPair(
  codes: Uint8Array,
  settings: PrimerDesignSettings = DEFAULT_PRIMER_SETTINGS
): { forward: DesignedPrimer | null; reverse: DesignedPrimer | null } {
  if (codes.length < settings.minLength * 2) {
    return { forward: null, reverse: null };
  }
  const window = Math.min(codes.length, 2000);
  const region = codes.subarray(0, window);
  const forward = pickPrimer(region, 1, 0, settings);
  const revCodes = reverseComplementCodes(region);
  const reversePick = pickPrimer(revCodes, -1, 0, settings);
  const reverse: DesignedPrimer | null = reversePick
    ? {
        ...reversePick,
        start: window - reversePick.end,
        end: window - reversePick.start,
      }
    : null;
  return { forward, reverse };
}

function pickPrimer(
  codes: Uint8Array,
  strand: 1 | -1,
  offset: number,
  settings: PrimerDesignSettings
): DesignedPrimer | null {
  let best: DesignedPrimer | null = null;
  let bestDelta = Infinity;
  const maxStart = Math.min(80, Math.max(0, codes.length - settings.minLength));
  for (let start = 0; start <= maxStart; start += 1) {
    for (let len = settings.minLength; len <= settings.maxLength; len += 1) {
      if (start + len > codes.length) break;
      const slice = codes.subarray(start, start + len);
      const gc = gcPercentCodes(slice);
      if (gc < settings.minGc || gc > settings.maxGc) continue;
      const tm = tmNearestNeighbor(slice, settings.concNmol);
      const delta = Math.abs(tm - settings.targetTm);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = {
          sequence: Array.from(slice, (code) => CODE_TO_CHAR[code]).join(''),
          start: offset + start,
          end: offset + start + len,
          tm,
          gc: Math.round(gc * 10) / 10,
          strand,
        };
      }
    }
  }
  return best;
}
